/** Questions only carry text + the correct category name now — every question's
 *  options are the theme's configured Answer Categories (Rules & Settings), so
 *  the CSV format doesn't need option_one..six / answer_id / showOption at all. */
export const QUIZ_CSV_HEADERS = ["question_name", "correct_answer"];

export const QUIZ_CSV_COLUMN_COUNT = QUIZ_CSV_HEADERS.length;

export const QUIZ_OPTION_MAX = 100;
export const QUIZ_QUESTION_MAX = 350;
export const QUIZ_PAGE_SIZES = [10, 25, 50, 100];

export const QUIZ_OPTION_KEYS = [
  "option_one",
  "option_two",
  "option_three",
  "option_four",
  "option_five",
  "option_six",
];

export const QUIZ_MIN_OPTIONS = 2;
export const QUIZ_MAX_OPTIONS = 6;
export const QUIZ_OPTION_COUNT_CHOICES = [2, 3, 4, 5, 6];

export function emptyQuizQuestion(id) {
  return {
    id,
    question_name: "",
    option_one: "",
    option_two: "",
    option_three: "",
    option_four: "",
    option_five: "",
    option_six: "",
    correct_answer: "",
    answer_id: "",
    showOption: 2,
  };
}

export const QUIZ_MAX_QUESTIONS = 100;
/** Minimum questions required for the game. */
export const QUIZ_MIN_QUESTIONS = 10;
/** Minimum questions required to persist to the theme database (0 allows clearing incomplete rows). */
export const QUIZ_SAVE_MIN_QUESTIONS = 0;

/** Sample category names — replace `correct_answer` with one of YOUR theme's
 *  actual configured Answer Categories when filling in your own rows. */
export const SAMPLE_CSV_ROWS = [
  [
    "All hiring panels include members from different backgrounds and communities.",
    "Diversity",
  ],
  [
    "Employees in the same role receive equal pay regardless of gender or ethnicity.",
    "Equity",
  ],
];

export function buildSampleCsvContent() {
  const escape = (val) => {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [
    QUIZ_CSV_HEADERS.join(","),
    ...SAMPLE_CSV_ROWS.map((row) => row.map(escape).join(",")),
  ];
  return lines.join("\r\n");
}

function downloadCsvBlob(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadSampleQuizCsv() {
  downloadCsvBlob(buildSampleCsvContent(), "quiz_questions_sample.csv");
}

export function exportQuizCsv(questions) {
  const escape = (val) => {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = (questions || []).map((q) => {
    const c = clampQuizFields(q);
    return [c.question_name, c.correct_answer].map(escape).join(",");
  });
  const content = [QUIZ_CSV_HEADERS.join(","), ...rows].join("\r\n");
  downloadCsvBlob(content, "quiz_questions_export.csv");
}

/** Parse a single CSV line respecting quoted fields */
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function normalizeHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

const HEADER_ALIASES = {
  question_name: "question_name",
  question: "question_name",
  question_text: "question_name",
  correct_answer: "correct_answer",
  correctanswer: "correct_answer",
  answer: "correct_answer",
  category: "correct_answer",
};

export const QUIZ_CSV_HEADER_REQUIRED_MESSAGE =
  "CSV cannot be uploaded without a header row. Row 1 must contain: question_name, correct_answer. correct_answer must exactly match one of your configured Answer Categories (Rules & Settings). Download Sample CSV for the correct format.";

function mapHeaderIndex(headers) {
  const index = {};
  headers.forEach((h, i) => {
    const key = HEADER_ALIASES[normalizeHeader(h)];
    if (key && index[key] === undefined) index[key] = i;
  });
  return index;
}

function looksLikeHeaderRow(cells) {
  const mapped = cells.map((c) => HEADER_ALIASES[normalizeHeader(c)] || normalizeHeader(c));
  const known = new Set(QUIZ_CSV_HEADERS);
  const matches = mapped.filter((m) => known.has(m)).length;
  return matches >= QUIZ_CSV_HEADERS.length;
}

function validateHeaderRow(headerCells) {
  if (!looksLikeHeaderRow(headerCells)) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }

  const col = mapHeaderIndex(headerCells);
  const missing = QUIZ_CSV_HEADERS.filter((k) => col[k] === undefined);
  if (missing.length) {
    throw new Error(
      `Invalid header row. Missing columns: ${missing.join(", ")}. Expected: ${QUIZ_CSV_HEADERS.join(", ")}`
    );
  }

  return col;
}

export function parseQuizCsv(text) {
  const raw = String(text ?? "").replace(/^\uFEFF/, "").trim();
  if (!raw) {
    throw new Error("CSV file is empty.");
  }

  const lines = raw.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }

  const headerCells = parseCsvLine(lines[0]);
  const col = validateHeaderRow(headerCells);

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    if (cells.every((c) => !c.trim())) continue;

    const cell = (key) => String(cells[col[key] ?? -1] ?? "").trim();
    const row = {
      question_name: cell("question_name"),
      correct_answer: cell("correct_answer"),
    };

    if (!row.question_name && !row.correct_answer) continue;
    rows.push(row);
  }

  if (!rows.length) {
    throw new Error("No question rows found in CSV (header row only).");
  }

  return rows;
}

function formatQuizRowMessage(rowLabel, text) {
  return rowLabel ? `${rowLabel} — ${text}` : text;
}

export function getQuizOptionValues(row) {
  return QUIZ_OPTION_KEYS.map((key) => String(row[key] ?? "").trim()).filter(Boolean);
}

/** Contiguous filled options from option_one upward (stops at first blank). */
export function getQuizOptionCount(row) {
  let count = 0;
  for (const key of QUIZ_OPTION_KEYS) {
    if (String(row?.[key] ?? "").trim()) count += 1;
    else break;
  }
  return count;
}

export function inferQuizOptionCount(row) {
  const count = getQuizOptionCount(row);
  if (count >= QUIZ_MIN_OPTIONS) return Math.min(QUIZ_MAX_OPTIONS, count);
  return QUIZ_MIN_OPTIONS;
}

/** Clear options beyond `count` and drop correct_answer if it was removed. */
export function applyQuizOptionCount(row, count) {
  const n = Math.min(QUIZ_MAX_OPTIONS, Math.max(QUIZ_MIN_OPTIONS, Number(count) || QUIZ_MIN_OPTIONS));
  const next = { ...row };
  QUIZ_OPTION_KEYS.forEach((key, i) => {
    if (i >= n) next[key] = "";
  });
  const options = getQuizOptionValues(next);
  const answer = String(next.correct_answer ?? "").trim();
  if (answer && !options.some((opt) => opt.toLowerCase() === answer.toLowerCase())) {
    next.correct_answer = "";
  }
  return next;
}

function validateOptionShape(row, rowLabel = "") {
  const vals = QUIZ_OPTION_KEYS.map((key) => String(row[key] ?? "").trim());
  let lastFilled = -1;
  for (let i = 0; i < vals.length; i++) {
    if (vals[i]) lastFilled = i;
  }

  if (lastFilled < QUIZ_MIN_OPTIONS - 1) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `At least ${QUIZ_MIN_OPTIONS} options are required (Option 1 and Option 2).`
      ),
    };
  }

  for (let i = 0; i <= lastFilled; i++) {
    if (!vals[i]) {
      return {
        ok: false,
        code: "VALIDATION",
        message: formatQuizRowMessage(
          rowLabel,
          "Options must be filled in order without gaps. Leave later options blank when using only 2 or 3 choices."
        ),
      };
    }
  }

  return { ok: true, count: lastFilled + 1 };
}

export function clampQuizFields(row) {
  const questionName = String(
    row?.question_name ?? row?.question ?? row?.question_text ?? ""
  )
    .trim()
    .slice(0, QUIZ_QUESTION_MAX);

  const option_one = String(row?.option_one ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_two = String(row?.option_two ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_three = String(row?.option_three ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_four = String(row?.option_four ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_five = String(row?.option_five ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_six = String(row?.option_six ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const correct_answer = String(row?.correct_answer ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const options = [option_one, option_two, option_three, option_four, option_five, option_six];
  const showFromOptions = options.filter(Boolean).length;
  const showOption = Math.max(
    QUIZ_MIN_OPTIONS,
    Math.min(QUIZ_MAX_OPTIONS, Number(row?.showOption) || showFromOptions || QUIZ_MIN_OPTIONS)
  );
  const answerIndex = options.findIndex(
    (opt) => opt && opt.toLowerCase() === correct_answer.toLowerCase()
  );

  return {
    question_name: questionName,
    option_one,
    option_two,
    option_three,
    option_four,
    option_five,
    option_six,
    correct_answer,
    answer_id: answerIndex >= 0 ? String(answerIndex + 1) : String(row?.answer_id ?? "").trim(),
    showOption,
  };
}

const QUIZ_CSV_IMPORT_FIELD_LIMITS = [
  { key: "question_name", label: "Question (question_name)", max: QUIZ_QUESTION_MAX },
  { key: "correct_answer", label: "Correct answer (correct_answer)", max: QUIZ_OPTION_MAX },
];

/** Mirror the theme's configured categories into option_one..six, exactly like the
 *  manual Add/Edit modal does — so an imported row saves through the same
 *  options-shaped validation as every other question. */
function optionFieldsFromCategories(categories) {
  const names = (categories || [])
    .map((c) => String(c?.name ?? "").trim())
    .filter(Boolean)
    .slice(0, QUIZ_MAX_OPTIONS);
  const fields = {};
  QUIZ_OPTION_KEYS.forEach((key, i) => {
    fields[key] = names[i] || "";
  });
  return fields;
}

function validateCorrectAnswer(row, rowLabel = "") {
  const options = getQuizOptionValues(row);
  const answer = String(row.correct_answer ?? "").trim();
  if (!answer) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Correct answer is required."),
    };
  }
  const normalizedAnswer = answer.toLowerCase();
  const match = options.find((opt) => opt.toLowerCase() === normalizedAnswer);
  if (!match) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        "Correct answer must exactly match one of the filled options."
      ),
    };
  }
  return { ok: true, value: match };
}

/**
 * Strict import checks for the simplified CSV format (question_name, correct_answer
 * only). correct_answer must exactly match one of the theme's configured Answer
 * Categories — the game no longer reads per-question options, so a CSV row that
 * names a category outside that list is rejected instead of silently accepted.
 */
export function validateQuizRowForImport(row, rowLabel = "Row", categories = []) {
  for (const { key, label, max } of QUIZ_CSV_IMPORT_FIELD_LIMITS) {
    const value = String(row[key] ?? "").trim();
    if (value.length > max) {
      return {
        ok: false,
        code: "CHAR_LIMIT",
        message: `${rowLabel} — ${label} has ${value.length} characters. Maximum allowed is ${max} characters. Please shorten the text and upload again.`,
      };
    }
  }

  const question_name = String(row.question_name ?? "").trim();
  if (!question_name) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Question name is required."),
    };
  }

  const answerRaw = String(row.correct_answer ?? "").trim();
  if (!answerRaw) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Correct answer is required."),
    };
  }

  const categoryNames = (categories || [])
    .map((c) => String(c?.name ?? "").trim())
    .filter(Boolean);
  const match = categoryNames.find((name) => name.toLowerCase() === answerRaw.toLowerCase());
  if (!match) {
    const list = categoryNames.length ? categoryNames.join(", ") : "none configured yet";
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `Correct answer "${answerRaw}" does not match any configured Answer Category (${list}). Fix this row or add/rename the category in Rules & Settings.`
      ),
    };
  }

  return {
    ok: true,
    data: {
      question_name: question_name.slice(0, QUIZ_QUESTION_MAX),
      correct_answer: match,
      ...optionFieldsFromCategories(categories),
    },
  };
}

export function validateQuizRow(row, rowLabel = "", options = {}) {
  const { requireQuestion = true } = options;
  const normalized = clampQuizFields(row);

  if (requireQuestion && !normalized.question_name) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        "Question text is required. Open Edit and fill in the Question field."
      ),
    };
  }

  const shape = validateOptionShape(normalized, rowLabel);
  if (!shape.ok) return shape;

  const answerCheck = validateCorrectAnswer(normalized, rowLabel);
  if (!answerCheck.ok) return answerCheck;

  const q = clampQuizFields({
    ...normalized,
    correct_answer: answerCheck.value,
  });

  return { ok: true, data: q };
}

export function validateQuizList(questions) {
  if (questions.length > QUIZ_MAX_QUESTIONS) {
    return { ok: false, message: `You can save up to ${QUIZ_MAX_QUESTIONS} questions.` };
  }

  if (!questions.length) {
    return { ok: true, data: [] };
  }

  const normalized = [];
  const missingQuestionIndexes = [];
  for (let i = 0; i < questions.length; i++) {
    const result = validateQuizRow(questions[i], `Question ${i + 1}`, {
      requireQuestion: true,
    });
    if (!result.ok) {
      if (/Question text is required/i.test(result.message)) {
        missingQuestionIndexes.push(i + 1);
        continue;
      }
      return { ok: false, message: result.message };
    }
    normalized.push(result.data);
  }

  if (missingQuestionIndexes.length) {
    const list = missingQuestionIndexes.join(", ");
    return {
      ok: false,
      message:
        missingQuestionIndexes.length === 1
          ? `Question ${list} is missing question text. Click Edit on that row, fill in Question, then save.`
          : `Questions ${list} are missing question text. Click Edit on each row, fill in Question, then save. You can also Delete incomplete rows and add them again.`,
    };
  }

  return { ok: true, data: normalized };
}
